<template>
  <div class="PlayerListStatusButtonView">
    <IconButton
      iconName="pencil"
      shape="circle"
      size="smedium"
      iconSize="smedium"
      :aria-label="`Edit ${player.name}’s status`"
      @click="handleClick"
    />
  </div>
</template>

<script lang="ts" setup>
import IconButton from "@/components/IconButton.vue";
import { RouteName } from "@/lib/router/routes.ts";
import useModalStore from "@/stores/useModalStore.ts";
import { PlayerSummary, RunSummary, StatusSummary } from "@shared/lib/database/query.types";
import pick from "@shared/lib/utils/object/pick";
import { EditPlayerStatusModalHistoryState } from "./modals/EditPlayerStatusModalView.types";

const {
  run,
  player,
  status,
} = defineProps<{
  run: RunSummary,
  player: PlayerSummary,
  status: StatusSummary | null,
}>();

const {
  openModal,
} = useModalStore();

function handleClick () {
  // TODO: util for only getting summary
  const state: EditPlayerStatusModalHistoryState = {
    player: pick(player, [
      "id",
      "name",
      "theme",
      "avatar_storage_path",
    ]),
    status,
  };

  openModal({
    name: RouteName.runPlayerStatus,
    params: {
      runId: run.id,
      playerId: player.id,
    },
  }, {
    state,
  });
}
</script>

<style lang="scss" scoped>
.PlayerListStatusButtonView {
  --itemHeight: 48px;
  --gap: 12px;
  display: flex;
  gap: var(--gap);
}

.PlayerListStatusButtonView__button {
  position: relative;

  &::before {
    position: absolute;
    inset-inline: calc(-1 * var(--gap) / 2);
    top: calc(50% - var(--itemHeight) / 2);

    height: var(--itemHeight);

    content: "";
  }
}

.PlayerListStatusButtonView__buttonIcon {
  position: relative;

  width: 24px;
  aspect-ratio: 1;

  border-radius: 50%;

  transition: 350ms opacity;

  &::after {
    position: absolute;
    inset: 0;

    border: solid 2px var(--themeColorNeutral900);
    border-radius: inherit;

    content: "";
  }

  &:disabled {
    opacity: 0.6;
  }

  .PlayerListStatusButtonView__button[aria-selected="true"] & {
    outline: solid 2px var(--themeColorNeutral900);
    outline-offset: 2px;
  }

  .PlayerListStatusButtonView__button[data-probability="1"] & {
    background-color: var(--colorOrange);
  }

  .PlayerListStatusButtonView__button[data-probability="0.5"] & {
    position: relative;
    background-color: var(--themeColorNeutral100);

    &::before {
      position: absolute;
      top: 0;
      left: 0;

      width: 50%;
      height: 100%;

      border-radius: 9999px 0 0 9999px;
      background-color: var(--colorOrange);

      content: "";
    }
  }

  .PlayerListStatusButtonView__button[data-probability="0"] & {
    background-color: var(--themeColorNeutral100);
  }

  .PlayerListStatusButtonView__button[data-probability="1"][aria-selected="false"] &,
  .PlayerListStatusButtonView__button[data-probability="1"]:disabled & {
    background-color: var(--themeColorNeutral400);
  }

  .PlayerListStatusButtonView__button[data-probability="0.5"][aria-selected="false"] &::before,
  .PlayerListStatusButtonView__button[data-probability="0.5"]:disabled &::before {
    background-color: var(--themeColorNeutral400);
  }
}
</style>
